import React from 'react';
import {
  Settings, Type, Hash, FileText, ToggleLeft, ListOrdered,
  Heart, Dice5, Columns, MousePointer, RectangleHorizontal,
  Lock, Eye
} from 'lucide-react';

export function GameSystemBuilderMockup() {
  const fieldTypes = [
    { icon: Type, label: 'text', color: 'text-blue-400 bg-blue-500/10' },
    { icon: Hash, label: 'number', color: 'text-green-400 bg-green-500/10' },
    { icon: FileText, label: 'multiline', color: 'text-cyan-400 bg-cyan-500/10' },
    { icon: ToggleLeft, label: 'checkbox', color: 'text-amber-400 bg-amber-500/10' },
    { icon: ListOrdered, label: 'select', color: 'text-purple-400 bg-purple-500/10' },
    { icon: Heart, label: 'resource', color: 'text-red-400 bg-red-500/10' },
    { icon: Dice5, label: 'dice', color: 'text-pink-400 bg-pink-500/10' },
    { icon: Columns, label: 'list', color: 'text-teal-400 bg-teal-500/10' },
  ];

  const systems = [
    { name: 'D&D 5e', version: '2024', builtin: true },
    { name: 'Wrath & Glory', version: '2.0', builtin: true },
    { name: 'Mon Système', version: '1.0', builtin: false },
  ];

  const sections = [
    { name: 'Identité', icon: '👤', fields: 4, access: 'read_write' },
    { name: 'Caractéristiques', icon: '⚔️', fields: 6, access: 'read_write' },
    { name: 'Combat', icon: '🛡️', fields: 5, access: 'read_write' },
    { name: 'Notes MJ', icon: '🔒', fields: 2, access: 'gm_only' },
  ];

  return (
    <div className="landing-mockup-card">
      <div className="flex items-center gap-2 mb-3">
        <Settings className="h-4 w-4 text-primary" />
        <span className="font-cinzel text-xs font-bold">Game Systems</span>
      </div>

      <div className="flex gap-1.5 mb-3 overflow-x-auto">
        {systems.map((sys, i) => (
          <div key={i} className={`flex items-center gap-1.5 text-[9px] px-2 py-1 rounded-lg border whitespace-nowrap cursor-pointer transition-colors ${i === 0 ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-muted/30 border-border/30 text-muted-foreground hover:bg-muted/50'}`}>
            <span className="font-medium">{sys.name}</span>
            <span className="text-[7px] opacity-60">v{sys.version}</span>
            {sys.builtin && <span className="text-[6px] px-1 py-0 rounded bg-primary/20 text-primary">built-in</span>}
          </div>
        ))}
      </div>

      <div className="relative rounded-lg border border-border/30 bg-muted/20 p-2 mb-3">
        <div className="flex items-center gap-1.5 mb-2">
          <MousePointer className="h-3 w-3 text-amber-400" />
          <span className="text-[9px] font-bold text-amber-400">PDF Builder</span>
          <span className="text-[7px] text-muted-foreground ml-auto">Page 1/3</span>
        </div>
        <div className="relative bg-white/5 rounded h-28 border border-border/20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-px bg-foreground mt-3 mx-3" />
            ))}
          </div>
          <div className="absolute top-2 left-2 w-[35%] h-5 border-2 border-blue-400/60 rounded-sm bg-blue-400/10 flex items-center px-1">
            <span className="text-[6px] text-blue-400 truncate">name (text)</span>
          </div>
          <div className="absolute top-2 right-2 w-[25%] h-5 border-2 border-green-400/60 rounded-sm bg-green-400/10 flex items-center px-1">
            <span className="text-[6px] text-green-400 truncate">level (number)</span>
          </div>
          <div className="absolute top-9 left-2 w-[18%] h-5 border-2 border-green-400/60 rounded-sm bg-green-400/10 flex items-center px-1">
            <span className="text-[6px] text-green-400 truncate">STR (1-30)</span>
          </div>
          <div className="absolute top-9 left-[22%] w-[18%] h-5 border-2 border-green-400/60 rounded-sm bg-green-400/10 flex items-center px-1">
            <span className="text-[6px] text-green-400 truncate">DEX (1-30)</span>
          </div>
          <div className="absolute top-9 left-[42%] w-[18%] h-5 border-2 border-green-400/60 rounded-sm bg-green-400/10 flex items-center px-1">
            <span className="text-[6px] text-green-400 truncate">CON (1-30)</span>
          </div>
          <div className="absolute top-16 left-2 w-[30%] h-5 border-2 border-red-400/60 rounded-sm bg-red-400/10 flex items-center px-1">
            <span className="text-[6px] text-red-400 truncate">hp (resource_bar)</span>
          </div>
          <div className="absolute top-16 left-[35%] w-[25%] h-5 border-2 border-amber-400/60 rounded-sm bg-amber-400/10 flex items-center px-1">
            <span className="text-[6px] text-amber-400 truncate">proficient (select)</span>
          </div>
          <div className="absolute bottom-2 left-2 w-[60%] h-6 border-2 border-cyan-400/60 rounded-sm bg-cyan-400/10 flex items-center px-1 border-dashed">
            <span className="text-[6px] text-cyan-400 truncate">backstory (multiline) — 🔒 MJ only</span>
          </div>
          <div className="absolute bottom-2 right-2 landing-draw-cursor">
            <RectangleHorizontal className="h-4 w-4 text-primary/40" />
          </div>
        </div>
      </div>

      <div className="mb-3">
        <div className="text-[9px] font-medium text-muted-foreground mb-1.5">8 types de champs</div>
        <div className="grid grid-cols-4 gap-1">
          {fieldTypes.map((ft, i) => {
            const Icon = ft.icon;
            return (
              <div key={i} className={`flex items-center gap-1 text-[7px] px-1.5 py-1 rounded ${ft.color}`}>
                <Icon className="h-2.5 w-2.5" />
                <span>{ft.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-1">
        <div className="text-[9px] font-medium text-muted-foreground mb-1">Sections du template</div>
        {sections.map((sec, i) => (
          <div key={i} className="flex items-center gap-2 text-[10px] px-2 py-1 rounded bg-muted/30 border border-border/20">
            <span>{sec.icon}</span>
            <span className="flex-1 font-medium">{sec.name}</span>
            <span className="text-[8px] text-muted-foreground">{sec.fields} champs</span>
            {sec.access === 'gm_only' ? (
              <span className="text-[7px] px-1 py-0 rounded bg-purple-500/20 text-purple-400 flex items-center gap-0.5"><Lock className="h-2 w-2" /> MJ</span>
            ) : (
              <span className="text-[7px] px-1 py-0 rounded bg-green-500/20 text-green-400 flex items-center gap-0.5"><Eye className="h-2 w-2" /> Tous</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
